// src/components/BoardControls.tsx
import React from "react";
import ColorSchemeSelector from "./ColorSchemeSelector.tsx";

interface BoardControlsProps {
  lightSquareColor: string;
  darkSquareColor: string;
  onLightColorChange: (color: string) => void;
  onDarkColorChange: (color: string) => void;
  vsComputer: boolean;
  aiColor: "white" | "black";
  aiSettings: any | null;
  onAiColorChange: (color: "white" | "black") => void;
  isAiThinking: boolean;
  moveHistoryLength: number;
  gameMode: "local" | "vsAI" | "online";
}

export const BoardControls: React.FC<BoardControlsProps> = ({
  lightSquareColor,
  darkSquareColor,
  onLightColorChange,
  onDarkColorChange,
  vsComputer,
  aiColor,
  aiSettings,
  onAiColorChange,
  isAiThinking,
  moveHistoryLength,
  gameMode,
}) => {
  return (
    <div className="board-controls-panel">
      <div className="board-controls-container">
        {/* Light Square Color Picker */}
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label
            htmlFor="light-color"
            style={{ fontWeight: "bold", color: "#333", fontSize: "13px" }}
          >
            Select Light Squares Color:
          </label>
          <input
            id="light-color"
            type="color"
            value={lightSquareColor}
            onChange={(e) => onLightColorChange(e.target.value)}
            style={{
              width: "35px",
              height: "35px",
              border: "2px solid #4CAF50",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
          <span
            style={{
              fontFamily: "monospace",
              fontSize: "12px",
              color: "#666",
              backgroundColor: "#fff",
              padding: "4px 8px",
              borderRadius: "3px",
              border: "1px solid #ddd",
            }}
          >
            {lightSquareColor}
          </span>
        </div>

        {/* Dark Square Color Picker */}
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label
            htmlFor="dark-color"
            style={{ fontWeight: "bold", color: "#333", fontSize: "13px" }}
          >
            Select Dark Squares Color:
          </label>
          <input
            id="dark-color"
            type="color"
            value={darkSquareColor}
            onChange={(e) => onDarkColorChange(e.target.value)}
            style={{
              width: "35px",
              height: "35px",
              border: "2px solid #4CAF50",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
          <span
            style={{
              fontFamily: "monospace",
              fontSize: "12px",
              color: "#666",
              backgroundColor: "#fff",
              padding: "4px 8px",
              borderRadius: "3px",
              border: "1px solid #ddd",
            }}
          >
            {darkSquareColor}
          </span>
        </div>

        {/* Color Scheme Selector */}
        <ColorSchemeSelector
          lightColor={lightSquareColor}
          darkColor={darkSquareColor}
          onColorChange={(light, dark) => {
            onLightColorChange(light);
            onDarkColorChange(dark);
          }}
        />

        {/* Note about saved preferences */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            backgroundColor: "#e8f5e9",
            borderRadius: "4px",
            border: "1px solid #c8e6c9",
            fontSize: "12px",
            color: "#2e7d32",
          }}
        >
          <span style={{ fontSize: "14px" }}>💾</span>
          <span style={{ fontWeight: "600" }}>
            Color preferences are saved to your account (limit: 12 schemes)
          </span>
        </div>

        {/* AI Controls - Only shown in AI mode */}
        {vsComputer && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "5px 10px",
              backgroundColor: "#fff3cd",
              borderRadius: "4px",
              border: "2px solid #ffc107",
            }}
          >
            <label
              htmlFor="ai-color"
              style={{
                fontWeight: "bold",
                color: "#333",
                fontSize: "14px",
                flexShrink: 0,
              }}
            >
              AI plays as:
            </label>
            <select
              id="ai-color"
              value={aiColor}
              onChange={(e) =>
                onAiColorChange(e.target.value as "white" | "black")
              }
              disabled={isAiThinking || moveHistoryLength > 0}
              style={{
                padding: "6px 10px",
                backgroundColor:
                  isAiThinking || moveHistoryLength > 0 ? "#e0e0e0" : "white",
                color: "#333",
                border: "2px solid #4CAF50",
                borderRadius: "4px",
                cursor:
                  isAiThinking || moveHistoryLength > 0
                    ? "not-allowed"
                    : "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                width: "110px",
                flexShrink: 0,
              }}
            >
              <option value="white">White</option>
              <option value="black">Black</option>
            </select>

            {!aiSettings && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#f44336",
                  maxWidth: "200px",
                  flexShrink: 0,
                }}
              >
                ⚠️ Configure AI in AI Settings tab first
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BoardControls;
